import { useState } from "react";
import type { FileUploadFieldDefinition } from "@rwaforms/shared";
import { useFileUpload } from "../hooks/useFileUpload";

interface FileUploadFieldProps {
  formId: string;
  field: FileUploadFieldDefinition;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  shareToken?: string; // Optional share token for public uploads
  onUploadStatusChange?: (isUploading: boolean) => void;
  onFileMetadataChange?: (metadata: Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>) => void;
}

export function FileUploadField({
  formId,
  field,
  value,
  onChange,
  disabled = false,
  shareToken,
  onUploadStatusChange,
  onFileMetadataChange,
}: FileUploadFieldProps): JSX.Element {
  const { uploadFile, progress } = useFileUpload();
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  // Track metadata locally to pass to parent
  const [filesMetadata, setFilesMetadata] = useState<Array<{
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>>([]);

  const [uploadedFiles, setUploadedFiles] = useState<string[]>(
    Array.isArray(value) ? value : value ? [value] : []
  );

  const maxFiles = field.maxFiles && field.maxFiles > 1 ? field.maxFiles : 1;
  const allowedTypes = field.allowedTypes?.length ? field.allowedTypes : undefined;
  const maxSizeBytes = field.maxFileSizeMB ? field.maxFileSizeMB * 1024 * 1024 : undefined;

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    // Validate file count
    if (maxFiles > 1) {
      const totalFiles = uploadedFiles.length + filesArray.length;
      if (totalFiles > maxFiles) {
        alert(`Maximum ${maxFiles} files allowed. You can upload ${maxFiles - uploadedFiles.length} more.`);
        return;
      }
    } else if (filesArray.length > 1) {
      alert("Only one file is allowed for this field.");
      return;
    }

    // Validate and upload each file
    for (const file of filesArray) {
      // Validate file type
      if (allowedTypes && allowedTypes.length > 0) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const mimeType = file.type;
        const isAllowed =
          allowedTypes.some((type) => {
            const normalizedType = type.toLowerCase().replace(/^\./, "");
            return (
              fileExtension === normalizedType ||
              mimeType === type ||
              mimeType.startsWith(`${normalizedType}/`)
            );
          });

        if (!isAllowed) {
          alert(`File type "${file.type || fileExtension}" is not allowed. Allowed types: ${allowedTypes.join(", ")}`);
          continue;
        }
      }

      // Validate file size
      if (maxSizeBytes && file.size > maxSizeBytes) {
        const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(2);
        alert(`File "${file.name}" is too large. Maximum size: ${maxSizeMB} MB`);
        continue;
      }

      // Upload file
      setUploadingFiles((prev) => {
        const next = new Set(prev).add(file.name);
        onUploadStatusChange?.(next.size > 0);
        return next;
      });

      try {
        const result = await uploadFile(file, {
          formId,
          shareToken,
          onProgress: (_progress) => {
            // Could show individual file progress here
          },
        });

        const newFiles = [...uploadedFiles, result.key];
        setUploadedFiles(newFiles);
        onChange(maxFiles > 1 ? newFiles : result.key);

        // Update metadata
        const newMetadata = [...filesMetadata, {
          fileName: result.filename,
          contentType: result.contentType,
          sizeBytes: result.size,
          tempKey: result.key,
        }];
        setFilesMetadata(newMetadata);
        onFileMetadataChange?.(newMetadata);

      } catch (error) {
        alert(`Failed to upload "${file.name}": ${error instanceof Error ? error.message : "Unknown error"}`);
      } finally {
        setUploadingFiles((prev) => {
          const next = new Set(prev);
          next.delete(file.name);
          onUploadStatusChange?.(next.size > 0);
          return next;
        });
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    onChange(maxFiles > 1 ? newFiles : "");

    // Update metadata
    const newMetadata = filesMetadata.filter((_, i) => i !== index);
    setFilesMetadata(newMetadata);
    onFileMetadataChange?.(newMetadata);
  };

  const label = (
    <div style={{ marginBottom: "0.5rem" }}>
      <p style={{ margin: 0, fontWeight: 600 }}>{field.title || "Untitled field"}</p>
      {field.description ? (
        <p style={{ margin: "0.2rem 0 0", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          {field.description}
        </p>
      ) : null}
      {field.maxFileSizeMB && (
        <p style={{ margin: "0.2rem 0 0", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          Max size: {field.maxFileSizeMB} MB
          {allowedTypes && allowedTypes.length > 0 && ` • Allowed types: ${allowedTypes.join(", ")}`}
        </p>
      )}
    </div>
  );

  const isUploading = uploadingFiles.size > 0;
  const canUploadMore = maxFiles > 1 ? uploadedFiles.length < maxFiles : uploadedFiles.length === 0;

  return (
    <div>
      {label}
      <div style={{ display: "grid", gap: "0.75rem" }}>
        {uploadedFiles.map((fileKey, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.5rem 0.75rem",
              background: "var(--color-bg)",
              borderRadius: "8px",
              border: "1px solid var(--color-border)",
            }}
          >
            <span style={{ fontSize: "0.9rem" }}>{fileKey.split("/").pop() || `File ${index + 1}`}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                style={{
                  padding: "0.25rem 0.5rem",
                  fontSize: "0.8rem",
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}

        {canUploadMore && (
          <div>
            <input
              type="file"
              multiple={maxFiles > 1}
              disabled={disabled || isUploading}
              accept={allowedTypes?.map((t) => (t.startsWith(".") ? t : `.${t}`)).join(",")}
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{
                width: "100%",
                padding: "0.5rem",
                fontSize: "0.9rem",
                borderRadius: "8px",
                border: "1px solid var(--color-border)",
              }}
            />
            {isUploading && (
              <div style={{ marginTop: "0.5rem" }}>
                <div
                  style={{
                    width: "100%",
                    height: "4px",
                    background: "var(--color-bg)",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${progress.percentage}%`,
                      height: "100%",
                      background: "var(--color-primary)",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                  Uploading... {progress.percentage}%
                </p>
              </div>
            )}
          </div>
        )}

        {progress.status === "error" && progress.error && (
          <p style={{ color: "var(--color-error)", fontSize: "0.85rem", margin: 0 }}>
            Upload error: {progress.error}
          </p>
        )}
      </div>
    </div>
  );
}
